import Sentry from '../errors/sentry';
import { CHUNK_RELOAD_KEY, chunkAssetUrl, registerChunkErrorHandler } from '../errors/chunk-error';

/**
 * Report a chunk error that survived its one reload.
 *
 * Matched chunk errors are dropped in Sentry's beforeSend, because the reload recovers them. The
 * signal worth keeping is the one that survives a reload — the user is on a dead page.
 *
 * Sent as a message rather than the error itself, which beforeSend would drop as a chunk error, so
 * the failing asset and the browser's own wording travel as context. The message text is what
 * groups these into one issue; keep it stable.
 *
 * @param {string} target The URL the reload was aimed at.
 * @param {unknown} [error] The chunk error that survived the reload.
 */
export const reportChunkReloadExhausted = (target, error) => {
	Sentry.captureMessage('chunk reload exhausted', {
		level: 'warning',
		tags: { target },
		contexts: {
			chunk_load: {
				asset_url: chunkAssetUrl(error),
				error_name: typeof error?.name === 'string' ? error.name : null,
				error_message: typeof error?.message === 'string' ? error.message : null,
			},
		},
	});
};

/**
 * Wire an entry's `unhandledrejection` hook to the chunk-error recovery, reporting the exhausted case
 * the same way in both entries.
 *
 * @param {() => string} resolveTarget Resolves the URL to reload onto, at the moment of failure.
 * @returns {() => void} Teardown that removes the listeners.
 */
export const registerChunkReload = (resolveTarget) => {
	return registerChunkErrorHandler(resolveTarget, reportChunkReloadExhausted);
};

/**
 * Refill the reload budget once a page has rendered, which proves its chunks loaded.
 */
export const clearChunkReloadFlag = () => {
	sessionStorage.removeItem(CHUNK_RELOAD_KEY);
};
